/**
 * Demo fixtures shaped like future CM-API surveillance responses.
 * Swap surveillanceClient to HTTP — leave this file for tests / demos.
 */

import type {
  SurveillanceEvent,
  SurveillanceGraph,
  SurveillanceMarket,
  SurveillanceSubGame,
} from './types'

export const STATIC_MEMBERS = [
  'player_alpha',
  'player_bravo',
  'player_charlie',
  'tm_north',
  'sm_delta',
] as const

export const STATIC_SUB_GAMES: SurveillanceSubGame[] = [
  { id: 'sg-cricket', name: 'Cricket' },
  { id: 'sg-soccer', name: 'Soccer' },
  { id: 'sg-tennis', name: 'Tennis' },
]

export const STATIC_EVENTS: SurveillanceEvent[] = [
  {
    id: 'ev-ind-aus',
    name: 'India v Australia',
    openDate: '2026-03-12T09:00:00.000Z',
    subGameId: 'sg-cricket',
  },
  {
    id: 'ev-eng-nz',
    name: 'England v New Zealand',
    openDate: '2026-02-01T10:00:00.000Z',
    subGameId: 'sg-cricket',
  },
  {
    id: 'ev-city-united',
    name: 'Man City v Man United',
    openDate: '2026-03-15T15:00:00.000Z',
    subGameId: 'sg-soccer',
  },
  {
    id: 'ev-djok-alca',
    name: 'Djokovic v Alcaraz',
    openDate: '2026-01-20T12:00:00.000Z',
    subGameId: 'sg-tennis',
  },
]

export const STATIC_MARKETS: SurveillanceMarket[] = [
  {
    id: 'mk-ind-aus-mo',
    name: 'Match Odds',
    betfairId: '1.23450001',
    eventId: 'ev-ind-aus',
    marketStartTime: '2026-03-12T09:30:00.000Z',
  },
  {
    id: 'mk-ind-aus-toss',
    name: 'Tied Match',
    betfairId: '1.23450002',
    eventId: 'ev-ind-aus',
    marketStartTime: '2026-03-12T09:30:00.000Z',
  },
  {
    id: 'mk-eng-nz-mo',
    name: 'Match Odds',
    betfairId: '1.23450011',
    eventId: 'ev-eng-nz',
    marketStartTime: '2026-02-01T10:30:00.000Z',
  },
  {
    id: 'mk-city-mo',
    name: 'Match Odds',
    betfairId: '1.23450021',
    eventId: 'ev-city-united',
    marketStartTime: '2026-03-15T15:00:00.000Z',
  },
  {
    id: 'mk-djok-mo',
    name: 'Match Odds',
    betfairId: '1.23450031',
    eventId: 'ev-djok-alca',
    marketStartTime: '2026-01-20T12:00:00.000Z',
  },
]

const T0 = Math.floor(new Date('2026-03-12T09:30:00.000Z').getTime() / 1000)

function rateSeries(base: number): [number, number][] {
  const pts: [number, number][] = []
  for (let i = 0; i < 48; i++) {
    const wobble = Math.sin(i / 4) * 0.12 + Math.cos(i / 7) * 0.05
    pts.push([T0 + i * 90, Number((base + wobble).toFixed(2))])
  }
  return pts
}

/** Graph keyed by market betfairId — Lab `{ m, h }` shape. */
export const STATIC_GRAPHS: Record<string, SurveillanceGraph> = {
  '1.23450001': {
    m: {
      c: 'ICC World Cup',
      e: 'India v Australia',
      m: 'Match Odds',
      mst: '2026-03-12T09:30:00.000Z',
      wn: 'India',
    },
    h: [
      {
        n: 'India',
        i: '111111',
        u: 'static://rates/1.23450001/111111.json',
        fu: 'static://rates/1.23450001/111111.json',
        rates: rateSeries(1.85),
        b: [
          {
            u: 'player_alpha',
            IP: '203.0.113.10',
            t: 'BACK',
            p: 1.82,
            s: 500,
            currencyType: 'INR',
            d: T0 + 600,
            h: '111111',
          },
          {
            u: 'player_bravo',
            IP: '203.0.113.22',
            t: 'LAY',
            p: 1.9,
            s: 250,
            currencyType: 'POINTS',
            d: T0 + 1200,
            h: '111111',
          },
          {
            u: 'player_charlie',
            t: 'BACK',
            p: 1.78,
            s: 1000,
            currencyType: 'INR',
            d: T0 + 2100,
            h: '111111',
          },
        ],
      },
      {
        n: 'Australia',
        i: '222222',
        u: 'static://rates/1.23450001/222222.json',
        fu: 'static://rates/1.23450001/222222.json',
        rates: rateSeries(2.1),
        b: [
          {
            u: 'player_bravo',
            t: 'BACK',
            p: 2.05,
            s: 300,
            currencyType: 'INR',
            d: T0 + 900,
            h: '222222',
          },
          {
            u: 'tm_north',
            t: 'LAY',
            p: 2.18,
            s: 800,
            currencyType: 'INR',
            d: T0 + 1800,
            h: '222222',
          },
        ],
      },
    ],
  },
  '1.23450011': {
    m: {
      c: 'Test Series',
      e: 'England v New Zealand',
      m: 'Match Odds',
      mst: '2026-02-01T10:30:00.000Z',
      wn: 'England',
    },
    h: [
      {
        n: 'England',
        i: '333333',
        u: 'static://rates/1.23450011/333333.json',
        fu: 'static://rates/1.23450011/333333.json',
        rates: rateSeries(1.72),
        b: [
          {
            u: 'player_alpha',
            t: 'BACK',
            p: 1.7,
            s: 400,
            currencyType: 'INR',
            d: T0 - 2_592_000,
            h: '333333',
          },
        ],
      },
      {
        n: 'New Zealand',
        i: '444444',
        u: 'static://rates/1.23450011/444444.json',
        fu: 'static://rates/1.23450011/444444.json',
        rates: rateSeries(2.25),
        b: [],
      },
    ],
  },
  '1.23450021': {
    m: {
      c: 'Premier League',
      e: 'Man City v Man United',
      m: 'Match Odds',
      mst: '2026-03-15T15:00:00.000Z',
      wn: 'Man City',
    },
    h: [
      {
        n: 'Man City',
        i: '555555',
        u: 'static://rates/1.23450021/555555.json',
        fu: 'static://rates/1.23450021/555555.json',
        rates: rateSeries(1.55),
        b: [
          {
            u: 'sm_delta',
            t: 'BACK',
            p: 1.52,
            s: 1200,
            currencyType: 'INR',
            d: T0 + 3 * 86_400,
            h: '555555',
          },
        ],
      },
      {
        n: 'Man United',
        i: '666666',
        u: 'static://rates/1.23450021/666666.json',
        fu: 'static://rates/1.23450021/666666.json',
        rates: rateSeries(2.6),
        b: [
          {
            u: 'player_charlie',
            t: 'LAY',
            p: 2.55,
            s: 150,
            currencyType: 'INR',
            d: T0 + 3 * 86_400 + 400,
            h: '666666',
          },
        ],
      },
    ],
  },
  '1.23450031': {
    m: {
      c: 'ATP',
      e: 'Djokovic v Alcaraz',
      m: 'Match Odds',
      mst: '2026-01-20T12:00:00.000Z',
      wn: 'Alcaraz',
    },
    h: [
      {
        n: 'Djokovic',
        i: '777777',
        u: 'static://rates/1.23450031/777777.json',
        fu: 'static://rates/1.23450031/777777.json',
        rates: rateSeries(2.4),
        b: [],
      },
      {
        n: 'Alcaraz',
        i: '888888',
        u: 'static://rates/1.23450031/888888.json',
        fu: 'static://rates/1.23450031/888888.json',
        rates: rateSeries(1.65),
        b: [
          {
            u: 'player_bravo',
            t: 'BACK',
            p: 1.62,
            s: 600,
            currencyType: 'INR',
            d: T0 - 4_320_000,
            h: '888888',
          },
        ],
      },
    ],
  },
}

/** Empty-ish graph for markets without a rich fixture. */
export function emptyGraphForMarket(market: SurveillanceMarket): SurveillanceGraph {
  const event = STATIC_EVENTS.find((e) => e.id === market.eventId)
  return {
    m: {
      c: 'Demo competition',
      e: event?.name ?? 'Event',
      m: market.name,
      mst: market.marketStartTime ?? new Date().toISOString(),
      wn: '—',
    },
    h: [],
  }
}
